import { FormEvent, useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  addDays,
  formatInputDate,
  startOfDay,
  startOfWeek,
} from '../calendars/calendarUtils';
import { ProfileTeamMembership } from '../../services/myCollaboratorApi';
import { TasksCalendarUser } from '../../services/tasksCalendarApi';
import { PLANNING_TASK_STATUSES } from '../../types/PlanningTaskStatus';

export type MyTasksDatePreset = 'week' | 'next30' | 'next90';

export type MyTasksKindFilter = '' | 'Meta' | 'Marco';

type MyTasksFiltersPanelProps = {
  fromDate: string;
  toDate: string;
  statusFilter: string;
  kindFilter?: MyTasksKindFilter;
  showKindFilter?: boolean;
  teams: ProfileTeamMembership[];
  selectedTeamIds: string[];
  visibleUsers: TasksCalendarUser[];
  selectedUserIds: string[];
  searchQuery: string;
  searchSuggestions: TasksCalendarUser[];
  canSearchPeople: boolean;
  onApplyRange: (from: string, to: string) => void;
  onStatusChange: (status: string) => void;
  onKindChange?: (kind: MyTasksKindFilter) => void;
  onTeamFilterChange: (teamIds: string[]) => void;
  onToggleUser: (userId: string) => void;
  onRemoveUser: (user: TasksCalendarUser) => void;
  onAddUser: (user: TasksCalendarUser) => void;
  onSearchQueryChange: (query: string) => void;
  onClear: () => void;
};

function parseInputDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
}

function daysBetween(from: string, to: string): number {
  const start = parseInputDate(from);
  const end = parseInputDate(to);
  if (!start || !end || end < start) return 0;
  return Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
}

export function rangeForTasksPreset(
  preset: MyTasksDatePreset,
  weekAnchor: Date = new Date(),
): { from: string; to: string } {
  const today = startOfDay(new Date());
  if (preset === 'week') {
    const start = startOfWeek(startOfDay(weekAnchor));
    return {
      from: formatInputDate(start),
      to: formatInputDate(addDays(start, 6)),
    };
  }

  const days = preset === 'next30' ? 30 : 90;
  return {
    from: formatInputDate(today),
    to: formatInputDate(addDays(today, days - 1)),
  };
}

export function MyTasksFiltersPanel({
  fromDate,
  toDate,
  statusFilter,
  kindFilter = '',
  showKindFilter = false,
  teams,
  selectedTeamIds,
  visibleUsers,
  selectedUserIds,
  searchQuery,
  searchSuggestions,
  canSearchPeople,
  onApplyRange,
  onStatusChange,
  onKindChange,
  onTeamFilterChange,
  onToggleUser,
  onRemoveUser,
  onAddUser,
  onSearchQueryChange,
  onClear,
}: MyTasksFiltersPanelProps) {
  const [draftStatus, setDraftStatus] = useState(statusFilter);
  const [draftKind, setDraftKind] = useState<MyTasksKindFilter>(kindFilter);

  useEffect(() => {
    setDraftStatus(statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    setDraftKind(kindFilter);
  }, [kindFilter]);

  const selectedDays = useMemo(() => daysBetween(fromDate, toDate), [fromDate, toDate]);
  const selectedPeople = useMemo(
    () => visibleUsers.filter((user) => selectedUserIds.includes(user.userId)),
    [visibleUsers, selectedUserIds],
  );
  const availablePeople = useMemo(
    () => visibleUsers.filter((user) => !selectedUserIds.includes(user.userId)),
    [visibleUsers, selectedUserIds],
  );

  function applyPreset(preset: MyTasksDatePreset): void {
    const range = rangeForTasksPreset(preset);
    onApplyRange(range.from, range.to);
  }

  function shiftWeek(deltaWeeks: number): void {
    const start = parseInputDate(fromDate) ?? startOfDay(new Date());
    const weekStart = startOfWeek(addDays(start, deltaWeeks * 7));
    onApplyRange(formatInputDate(weekStart), formatInputDate(addDays(weekStart, 6)));
  }

  function handleApply(event: FormEvent): void {
    event.preventDefault();
    onStatusChange(draftStatus);
    if (showKindFilter) {
      onKindChange?.(draftKind);
    }
    onApplyRange(fromDate, toDate);
  }

  function handleClear(): void {
    setDraftStatus('');
    setDraftKind('');
    onClear();
  }

  return (
    <form
      onSubmit={handleApply}
      className="mb-4 space-y-5 rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm"
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-semibold text-secondary-900">Período</p>
          {selectedDays > 0 ? (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
              {selectedDays} dia{selectedDays === 1 ? '' : 's'} selecionado
              {selectedDays === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="erp-btn-secondary text-sm" onClick={() => shiftWeek(-1)}>
              ← Semana anterior
            </button>
            <button
              type="button"
              className="erp-btn-secondary text-sm"
              onClick={() => applyPreset('week')}
            >
              Hoje
            </button>
            <button type="button" className="erp-btn-secondary text-sm" onClick={() => shiftWeek(1)}>
              Próxima semana →
            </button>
          </div>
          <div className="hidden h-8 w-px bg-secondary-200 sm:block" aria-hidden />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="erp-btn-secondary text-sm"
              onClick={() => applyPreset('week')}
            >
              Esta semana
            </button>
            <button
              type="button"
              className="erp-btn-secondary text-sm"
              onClick={() => applyPreset('next30')}
            >
              Próximos 30 dias
            </button>
            <button
              type="button"
              className="erp-btn-secondary text-sm"
              onClick={() => applyPreset('next90')}
            >
              Próximos 90 dias
            </button>
          </div>
        </div>
      </div>

      <div
        className={`grid gap-4 border-t border-secondary-100 pt-4 ${
          showKindFilter ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'
        }`}
      >
        {showKindFilter ? (
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-secondary-800">Tipo</span>
            <select
              className="erp-input"
              value={draftKind}
              onChange={(event) => setDraftKind(event.target.value as MyTasksKindFilter)}
            >
              <option value="">Metas e marcos</option>
              <option value="Meta">Somente metas</option>
              <option value="Marco">Somente marcos</option>
            </select>
          </label>
        ) : null}

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-secondary-800">Status</span>
          <select
            className="erp-input"
            value={draftStatus}
            onChange={(event) => setDraftStatus(event.target.value)}
          >
            <option value="">Todos</option>
            {PLANNING_TASK_STATUSES.map((status) => (
              <option key={status.id} value={status.id}>
                {status.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-secondary-800">Equipes</span>
          <select
            className="erp-input"
            value={selectedTeamIds.length === 1 ? selectedTeamIds[0] : ''}
            disabled={teams.length === 0}
            onChange={(event) => {
              const next = event.target.value;
              onTeamFilterChange(next ? [next] : []);
            }}
          >
            <option value="">Todas as equipes</option>
            {teams.map((team) => (
              <option key={team.teamId} value={team.teamId}>
                {team.teamName}
              </option>
            ))}
          </select>
          {teams.length === 0 ? (
            <span className="mt-1 block text-xs text-secondary-500">Nenhuma equipe vinculada.</span>
          ) : null}
        </label>

        <div className="min-w-0 text-sm">
          <span className="mb-1 block font-medium text-secondary-800">Pessoas</span>
          <div className="erp-input flex min-h-[42px] flex-wrap items-center gap-1.5 py-1.5">
            {selectedPeople.map((user) => (
              <span
                key={user.userId}
                className="inline-flex items-center gap-1 rounded-full border border-secondary-200 bg-secondary-50 py-0.5 pl-2.5 pr-1 text-xs text-secondary-800"
              >
                {user.name}
                {user.isSelf ? ' (você)' : null}
                {user.isSelf ? null : (
                  <button
                    type="button"
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-secondary-500 hover:bg-secondary-200 hover:text-secondary-800"
                    aria-label={`Remover ${user.name}`}
                    onClick={() => onRemoveUser(user)}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )}
              </span>
            ))}
            {canSearchPeople ? (
              <div className="relative min-w-[10rem] flex-1">
                <input
                  className="w-full border-0 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-secondary-400"
                  value={searchQuery}
                  onChange={(event) => onSearchQueryChange(event.target.value)}
                  placeholder="Adicionar pessoas"
                />
                {searchSuggestions.length > 0 ? (
                  <ul className="absolute left-0 right-0 z-20 mt-2 max-h-48 overflow-auto rounded-lg border border-secondary-200 bg-white shadow-lg">
                    {searchSuggestions.map((user) => (
                      <li key={user.userId}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-secondary-50"
                          onClick={() => onAddUser(user)}
                        >
                          {user.name}
                          {user.isSelf ? ' (você)' : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
          {availablePeople.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {availablePeople.map((user) => (
                <button
                  key={user.userId}
                  type="button"
                  className="rounded-full border border-dashed border-secondary-300 px-2.5 py-0.5 text-xs text-secondary-600 hover:border-primary-400 hover:text-primary-700"
                  onClick={() => onToggleUser(user.userId)}
                >
                  + {user.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-secondary-100 pt-4">
        <button
          type="button"
          className="text-sm font-medium text-secondary-600 hover:text-secondary-900"
          onClick={handleClear}
        >
          Limpar filtros
        </button>
        <button type="submit" className="erp-btn-primary">
          Aplicar filtros
        </button>
      </div>
    </form>
  );
}
